import { useMemo } from 'react';
import {
  PUBLISHER_INVENTORY_METRICS,
  type PublisherInventoryRow,
  rollupPublisherInventory,
} from '../lib/publisherInventory';
import { usePublishersDirectory } from './usePublishersDirectory';
import { useReportWidget } from './useReportWidget';

/** Pooled server-side ratios (Lucas, review) — see publisherInventory.ts's own doc comment on why these are requested directly rather than re-derived from the summed base metrics. */
const RATIO_METRICS = ['cpm', 'ctr', 'frequency', 'vcr', 'client_cpm'] as const;

/**
 * Raw per-bundle/per-domain rows capped at the API's own page-size ceiling
 * (`queryBuilder.ts` clamps `pagination.limit` to 1000). Legacy's Publisher
 * report (`BasePublisherReportService.reportMaxRows`) caps the FINAL
 * aggregated publisher list instead — its BI-tool query isn't paged on the
 * way in. Capping here instead means an org with more than 1000 distinct app
 * bundles (or domains) in the selected filters undercounts the long tail;
 * sorting by impressions descending keeps the error to the smallest rows.
 */
const RAW_ROW_LIMIT = 1000;

/**
 * Powers the Impressions by Type -> Inventory -> Publishers view (flank#4643).
 *
 * The bundle->publisher rollup itself now happens server-side (ribeye-api's
 * `ribeye_publisher` dimension, fixing the flank#4644 conflict: that gate
 * strips raw `app_name` rows for every non-admin caller, which used to break
 * this view for exactly the users it's meant to default-serve). This hook
 * fetches the already-grouped publisher totals (which doesn't carry raw
 * bundle/app_name text, so isn't gated) plus the live Publisher directory.
 * The one remaining raw-`app_name` fetch is `appDetails`, kept ONLY to
 * populate the expanded row's per-bundle/DSP breakdown, which was always
 * separately gated behind `canViewPlatform` (#4442) and stays that way;
 * `enabled` on that fetch skips it entirely for a viewer who could never see
 * it rendered anyway. Domains are deliberately not fetched here at all — see
 * publisherInventory.ts's module doc comment (double-counts GAM delivery
 * against the app-bundle totals; Lucas, review).
 */
export const usePublisherInventory = (enabled: boolean, canViewPlatform: boolean) => {
  const publisherTotals = useReportWidget('inventoryPublishersTotals', {
    dimensions: ['ribeye_publisher'],
    metrics: [...PUBLISHER_INVENTORY_METRICS, ...RATIO_METRICS],
    sort: [{ field: 'impressions', dir: 'desc' }],
    limit: RAW_ROW_LIMIT,
    enabled,
  });
  const appDetails = useReportWidget('inventoryPublishersAppDetails', {
    dimensions: ['app_bundle', 'app_name', 'dsp'],
    metrics: [...PUBLISHER_INVENTORY_METRICS, ...RATIO_METRICS],
    limit: RAW_ROW_LIMIT,
    enabled: enabled && canViewPlatform,
  });
  const { publishers, loading: publishersLoading, error: publishersError } = usePublishersDirectory(enabled);

  const rows: PublisherInventoryRow[] = useMemo(
    () => (enabled ? rollupPublisherInventory(publisherTotals.rows, appDetails.rows, publishers) : []),
    [enabled, publisherTotals.rows, appDetails.rows, publishers],
  );

  // `ribeye_publisher`'s grouped row count is almost never near the page cap
  // even when the raw bundle fetch behind it (server-side) is — the server
  // flags that case explicitly (`truncated_fields:
  // ["dimensions.ribeye_publisher"]`) rather than leave it to a row-count
  // comparison that would rarely trigger. This is the table-wide warning:
  // whether the TOTALS (the server-grouped rows above) might be undercounted.
  const truncated = publisherTotals.truncatedFields.includes('dimensions.ribeye_publisher');
  // appDetails' own cap (1000 app×DSP rows, common) only ever omits bundles
  // from an EXPANDED row's breakdown — the top-level totals come from
  // publisherTotals' own rollup, not from summing appDetails. Surfaced
  // separately so the table can flag it on the expanded rows themselves
  // rather than flip the table-wide "totals may be undercounted" warning for
  // a cap that never affects those totals (Lucas, review).
  const appDetailsTruncated = appDetails.totalRows > appDetails.rows.length;
  const restrictedFields = [...new Set([...publisherTotals.restrictedFields, ...appDetails.restrictedFields])];

  // appDetails' own loading/error (review, CodeRabbit): omitted before, so a
  // failed or still-in-flight appDetails fetch left expanded rows showing an
  // empty bundle list with no error and no spinner. Gated on canViewPlatform,
  // matching `enabled` above — the request never fires without it.
  //
  // Kept OUT of the table-wide `error` (review, CodeRabbit): the publisher
  // directory and appDetails both only feed the expanded row's app/DSP
  // breakdown — every top-level row's name/logo/totals comes straight off
  // `publisherTotals`'s own server-grouped rows. Folding either failure into
  // the shared error hid every successful publisher row over a failure in
  // detail nobody was looking at yet.
  return {
    rows,
    loading: publisherTotals.loading || publishersLoading || (canViewPlatform && appDetails.loading),
    error: publisherTotals.error,
    appDetailsError: publishersError || (canViewPlatform ? appDetails.error : null),
    restrictedFields,
    truncated,
    appDetailsTruncated,
  };
};
